/**
 * Firestore access for share-card template overrides.
 *
 * Collection: shareCardTemplates/{templateId} — one doc per overridden
 * template, admin-authored. Missing docs mean "use the code default".
 */

import { collection, deleteDoc, doc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import {
  DEFAULT_SHARE_CARD_TEMPLATES,
  mergeTemplateOverride,
  type ShareCardTemplateContent,
  type ShareCardTemplateId,
  type ShareCardTemplates,
} from '../shareCards/shareCardTemplates';

const COLLECTION = 'shareCardTemplates';

/** Fetch overrides and merge over defaults. Any failure returns the defaults —
 *  sharing must never break because copy couldn't load. */
export async function fetchShareCardTemplates(): Promise<ShareCardTemplates> {
  const merged: ShareCardTemplates = { ...DEFAULT_SHARE_CARD_TEMPLATES };
  try {
    const snap = await getDocs(collection(db, COLLECTION));
    snap.forEach((docSnap) => {
      const id = docSnap.id as ShareCardTemplateId;
      if (id in merged) {
        merged[id] = mergeTemplateOverride(merged[id], docSnap.data());
      }
    });
  } catch {
    // defaults already in place
  }
  return merged;
}

/** Which template ids currently have a Firestore override (admin UI). */
export async function fetchShareCardTemplateOverrideIds(): Promise<Set<string>> {
  const ids = new Set<string>();
  const snap = await getDocs(collection(db, COLLECTION));
  snap.forEach((docSnap) => ids.add(docSnap.id));
  return ids;
}

/** Admin: write an override for one template. */
export async function saveShareCardTemplate(
  id: ShareCardTemplateId,
  content: ShareCardTemplateContent,
  updatedBy?: string
): Promise<void> {
  await setDoc(doc(db, COLLECTION, id), {
    ...content,
    updatedAt: serverTimestamp(),
    ...(updatedBy ? { updatedBy } : {}),
  });
}

/** Admin: remove the override so the template falls back to the code default. */
export async function resetShareCardTemplate(id: ShareCardTemplateId): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}
